<template>
  <div class="search">
    <meta-search
      v-if="enableMeta"
      :filter-query-object="filterQueryObject"
      :param-timestamp="paramTimestamp"
      :param-total="total"
    />
    <search-filter-bar :filter-query-object="filterQueryObject" :filter-path="filterPath" event-source="quick_filter" />
    <div class="container">
      <slot name="breadcrumbs" />
      <slot name="topContent" />
      <job-list
        class="search__list"
        :source="source"
        :bidding-id="lastBiddingId"
        :bidding-session="biddingSession"
        :error-text="errorText"
        :jobs="jobList"
        :request-id="requestId"
        :tracking-params="trackingParams"
        :session-id="session.search"
        :show-error="isError"
        :title="title"
      />
      <loading v-show="canLoadMore" id="trigger" />
    </div>
  </div>
</template>

<script>
import { getTrackingSearchParams, sendFilterApplyEvent } from '../../libs/tracking/utils/event'
import { interleave, sliceByInt } from '../../libs/data/transform'
import BiddingMixin from '../../mixins/bidding'
import { createSessionId } from '../../libs/tracking/utils/session'
import GraphqlFormMixin from '../../mixins/form/graphql'
import InfinityScrollMixin from '../../mixins/list/infinityScroll'
import LoadingIcon from '../loader/loadingIcon'
import LoadingMixin from '../../mixins/loading'
import MenuMixin from '../../mixins/menu/section'
import { NEARBY_L_KEY } from '../../libs/mapping/nearby'
import { QUERY_JOBS } from '../../api/octo/graphql/queries'
import SearchCachingMixin from '../../mixins/search/caching'
import SearchFilterBar from '../../components/search/searchFilterBar'
import SearchFilterMixin from '../../mixins/search/filter'
import SearchMetadata from './metadata/searchMetaData'
import TitledJobList from '../list/titledJobList'

const navigator = typeof window !== 'undefined' && typeof window.navigator !== 'undefined' ? window.navigator : null

export default {
  beforeDestroy() {
    // Add cache when leave a page
    this.addCache(this.$data, this.lastFullPath)
  },
  components: {
    'job-list': TitledJobList,
    loading: LoadingIcon,
    'meta-search': SearchMetadata,
    'search-filter-bar': SearchFilterBar,
  },
  computed: {
    /**
     * Whether there are more jobs to be loaded
     * @returns {boolean}
     */
    canLoadMore() {
      return this.total > this.numberOfJobs
    },

    /**
     * Search error text
     * @returns {string}
     */
    errorText() {
      if (this.isNearByFail) {
        return this.$t('search.nearByDisable')
      }
      return ''
    },

    /**
     * Filter apply's Source
     */
    filterApplySource() {
      return this.$route.query.source
    },

    /**
     * Whether there is search error
     * @returns {boolean}
     */
    isError() {
      return !this.isLoading && (this.jobList.length === 0 || !!this.errorText)
    },

    /** Number of loaded jobs */
    numberOfJobs() {
      return this.organicJobs.length
    },

    /**
     * Request offset
     */
    offset() {
      return this.numberOfJobs
    },

    /**
     * Search result title
     * @returns {string}
     */
    title() {
      // On page load, do not show title
      // unless it's static generated page,
      // otherwise will cause virtal DOM hydration error
      if (!this.isLoading) {
        const allParams = this.titleConfig.params || {}
        if (this.titleConfig.hasTotalJobs) {
          allParams.total = this.totalJobs
        }
        return this.$tc(this.titleConfig.title, this.totalJobs, allParams)
      }
      return ''
    },

    /**
     * Total number of jobs
     */
    totalJobs() {
      return this.total
    },

    /**
     * Event tracking parameters
     **/
    trackingParams() {
      return getTrackingSearchParams(this.filterQueryObject)
    },
  },
  created() {
    // NOTE: Client side only
    // Search result should be available
    // on server side static generation,
    // so only need to init search page on client side
    if (process.client) {
      if (this.hasCache() && this.enableJobAds) {
        // If cache available, refresh ads directly
        // Remarks: avoid to open blocking loading component to prevent scroll position recovery
        this.refreshJobAds()
      } else {
        // Otherwise init job search
        this._initData()
      }
    }
  },
  data() {
    // Recover to cache in client side
    if (process.client) {
      const cache = this.getSearchCache()
      if (cache) {
        return cache
      }
    }

    // Default Data
    return {
      /** Graphql queries */
      gqlQueries: {
        jobSearch: QUERY_JOBS,
      },

      /** Whether getting current position failed */
      isNearByFail: false,

      /**
       * List of jobs to display,
       * initialized w/ parent props
       */
      jobList: this.paramJobs,

      /**
       * List of job page,
       * initialized w/ parent props
       */
      jobPages: this.paramJobs.length ? [this.paramJobs] : [],

      /** Job Type IDs */
      jobType: [],

      /** Cache current full path for loading search cache */
      // $route.fulPath is changed before calling beforeDestroy(). So, need to cache it
      lastFullPath: this.$route.fullPath,

      /** Number of entries per request */
      limit: 20,

      /** List of organic jobs */
      organicJobs: [],

      /** Number of organic jobs between ads */
      organicJobsPerBatch: 10,

      /** Session Id */
      session: {
        search: '',
      },

      /** whether to display the multiple level select filter */
      showMultiSelectPanel: false,

      /**
       * Total number of search results,
       * initialized w/ parent props
       */
      total: this.paramTotal,
    }
  },
  methods: {
    /**
     * Initialize job search result
     * @private
     */
    _initData() {
      this.openLoadingDialog()

      if (this.$route.query.spk) {
        // Handle special case search
        this.handleSpecificSearch(this.$route.query.spk)
      } else {
        // Start job search and ad requests
        this.$tokenReady(this._initQueryJobAndJobAds)
      }
    },

    /**
     * Handle job search and ad requests
     * @private
     */
    async _initQueryJobAndJobAds() {
      if (this.enableJobAds) {
        // Wait for search and ad requests to complete
        await Promise.all([this._queryJobsIfUnavailable(), this._loadAds()])
        this.jobList = interleave(this.jobPages, this.biddingAdsPages)
      } else {
        this.jobList = await this._queryJobsIfUnavailable()
      }
      this.closeLoadingDialog()
    },

    /**
     * Handle job ad request
     * @private
     */
    _loadAds() {
      return this.loadAds('search_job_result_list', this.filterQueryObject)
    },

    /**
     * Handle job search request
     * @param isRefresh
     * @returns {Promise<Array>}
     * @private
     */
    async _queryJobs(isRefresh = false) {
      let jobs = []
      const res = await this.submitGql('jobSearch', true)
      // Extract jobs result from response
      if (res && res.data && res.data.job_search) {
        // Refresh search session if needed
        if (isRefresh) {
          this.session.search = createSessionId()
          this.jobPages = []
          this.organicJobs = []
        }
        const searchResult = res.data.job_search
        if (searchResult && Array.isArray(searchResult.result)) {
          this.total = searchResult.total
          jobs = searchResult.result
          this.organicJobs = this.organicJobs.concat(jobs)
          this.jobPages = sliceByInt(this.organicJobs, [this.organicJobsPerBatch], true)
        }
      }
      return jobs
    },

    /**
     * Handle job search request,
     * if search result isn't available yet
     * @returns {Promise<Array>}
     * @private
     */
    _queryJobsIfUnavailable() {
      // send filter apply event

      sendFilterApplyEvent(this.$eventTracker, {
        source: this.filterApplySource,
        trackingSearchParams: getTrackingSearchParams(this.filterQueryObject),
      })

      // If search result already exists
      if (this.organicJobs.length) {
        // Refresh search session
        this.session.search = createSessionId()
        // Return existing jobs
        return this.organicJobs
      }

      // Otherwise start new job search request
      return this._queryJobs(true)
    },

    /**
     * Clear search results
     */
    clearData() {
      this.isNearByFail = false
      this.jobList = []
      this.jobPages = []
      this.organicJobs = []
      this.total = 0
    },

    /**
     * Handle getting user current location
     * @param {Function} callback
     */
    getLocation(callback) {
      if (!navigator || !navigator.geolocation) {
        callback(null)
        return
      }
      // get position
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          callback(pos.coords)
        },
        function () {
          callback(null)
        },
      )
    },

    /**
     * Handle nearby job search based on user location
     * @param {Coordinates} geoPos
     */
    handleLocation(geoPos) {
      if (geoPos) {
        const query = {
          latitude: geoPos.latitude,
          longitude: geoPos.longitude,
        }
        this.$router.replace({
          path: this.localePathName(this.filterPath),
          query: {
            ...query,
            source: this.filterApplySource,
          },
        })
        this.$eventTracker.setUserProperties({
          latlng: `${geoPos.latitude},${geoPos.longitude}`,
        })
      } else {
        // No position found
        this.isNearByFail = true
        this.closeLoadingDialog()
      }
    },

    /**
     * Handle special search cases
     */
    handleSpecificSearch(specificKey) {
      if (specificKey === NEARBY_L_KEY) {
        // Nearby
        this.getLocation(this.handleLocation)
      }
    },

    /**
     * Handle loading more search results
     */
    onLoadMore() {
      // Wait for token ready to prevent load more trigger when server-generated list
      return new Promise((resolve, reject) => {
        this.$tokenReady(async () => {
          if (this.canLoadMore) {
            await this._queryJobs(false)
            this.jobList = interleave(this.jobPages, this.biddingAdsPages)
          }
          resolve(null)
        })
      })
    },

    /**
     * Handle refreshing job ads
     */
    async refreshJobAds() {
      await this._loadAds()
      this.jobList = interleave(this.jobPages, this.biddingAdsPages)
    },
  },
  mixins: [
    BiddingMixin,
    GraphqlFormMixin,
    SearchCachingMixin,
    SearchFilterMixin,
    InfinityScrollMixin,
    LoadingMixin,
    MenuMixin,
  ],
  props: {
    enableJobAds: {
      default: true,
      type: Boolean,
    },
    enableMeta: {
      default: false,
      type: Boolean,
    },
    // The path that we want to visit after applying filter
    // For example, filter by search filter bar and nearby location
    filterPath: {
      default: '',
      type: String,
    },
    filterQueryObject: {
      default: () => ({}),
      type: Object,
    },
    /**
     * List of jobs matching search param
     */
    paramJobs: {
      default: () => [],
      type: Array,
    },

    /**
     * Generated timestamp for seo (timeInMs)
     */
    paramTimestamp: {
      default: 0,
      type: Number,
    },

    /**
     * Total number of jobs matching search param
     */
    paramTotal: {
      default: 0,
      type: Number,
    },
    /**
     * Source for event tracking
     */
    source: {
      default: '',
      type: String,
    },
    /**
     * @typedef {Object} titleConfig
     * @property {boolean} hasTotalJobs - Show the total numbers of jobs in title or not
     * @property {object} [params] - Parameter required for the title text in locale files
     * @property {string} title - Key of the title text in locale files
     */
    titleConfig: {
      default: () => ({}),
      type: Object,
    },
  },
  watch: {
    /**
     * Handle route change
     */
    $route(to, from) {
      // Remarks: Not support back to last query and use cache

      // Handle query's filter change
      this.lastFullPath = to.fullPath
      this.clearData()
      this._initData()
    },
  },
}
</script>

<style lang="postcss" scoped>
.search {
  & .container {
    @apply mx-auto;
  }
}

.search__list {
  @apply w-full;
}

@screen desktop {
  .search__list::v-deep {
    & .list__job {
      @apply w-1/2 py-2;
    }

    & .list__job .jc {
      max-width: none;
    }
  }
}
</style>
